import React, { useState, useEffect } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Plus, Clock, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '../components/shared/Modal';
import PermissionGate from '../components/shared/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

const CATEGORIES = [
  'Features & Modules',
  'Approval Workflow & Escalation',
  'Cost Centres',
  'Trip & Sector Types',
  'Fees & Billing',
  'Travel Policy Rates',
  'Hotel Booking',
  'Policy Variance Thresholds',
  'Security & Two-Factor Authentication',
  'Annual Budget',
  'Other',
];

const FEATURE_LABELS = {
  hotelBooking:         'Hotel booking',
  invoiceGeneration:    'Invoice generation',
  reports:              'Reports',
  accessibilityToolbar: 'Accessibility toolbar',
  fileAttachments:      'File attachments',
  selfManagedTrips:     'Self-managed trips',
  accommodationPolicy:  'Accommodation policy reporting',
  flightPolicy:         'Flight cost policy reporting',
  customPermissions:    'Custom user permissions',
  carbonTracking:       'Carbon footprint tracking',
};

function Section({ title, children, collapsible = false }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => collapsible && setOpen(v => !v)}
        className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-gray-100 ${collapsible ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
      >
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
        {collapsible && (open
          ? <ChevronDown size={14} className="text-gray-400" aria-hidden="true" />
          : <ChevronRight size={14} className="text-gray-400" aria-hidden="true" />
        )}
      </button>
      {open && <div className="px-5 py-4 space-y-3">{children}</div>}
    </div>
  );
}

function FieldRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start gap-4 text-sm">
      <span className="w-48 shrink-0 text-gray-500">{label}</span>
      <span className={`text-gray-800 font-medium ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function FeatureBadge({ label, enabled }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {enabled ? '✓' : '–'} {enabled ? 'On' : 'Off'}
      </span>
      <span className="text-gray-700">{label}</span>
    </div>
  );
}

function Tags({ values }) {
  if (!values?.length) return <span className="text-sm text-gray-500">None configured</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map(v => (
        <span key={v} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">{v}</span>
      ))}
    </div>
  );
}

function RequestStatusBadge({ status }) {
  if (status === 'pending')  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><Clock size={11} aria-hidden="true" /> Pending review</span>;
  if (status === 'approved') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle2 size={11} aria-hidden="true" /> Approved</span>;
  if (status === 'rejected') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"><XCircle size={11} aria-hidden="true" /> Not approved</span>;
  return null;
}

function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function RequestModal({ clientId, clientName, userProfile, onClose }) {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return setError('Please describe what you\'d like changed.');
    setSaving(true);
    setError('');
    try {
      const name = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ') || userProfile.email;
      const reqRef = await addDoc(collection(db, 'configRequests'), {
        clientId,
        clientName: clientName || clientId,
        requestedBy:      userProfile.uid,
        requestedByName:  name,
        requestedByEmail: userProfile.email || '',
        category,
        description: description.trim(),
        status:    'pending',
        reviewedBy:   null,
        reviewedByName: null,
        reviewedAt:   null,
        reviewNote:   null,
        createdAt: serverTimestamp(),
      });
      // Queue notification email to STX staff
      await addDoc(collection(db, 'emailQueue'), {
        type:        'config_change_request',
        clientId,
        requestId:   reqRef.id,
        clientName:  clientName || clientId,
        userName:    name,
        userEmail:   userProfile.email || '',
        category,
        description: description.trim(),
        status:      'pending',
        scheduledFor: new Date(),
        createdAt:   serverTimestamp(),
      });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Request a configuration change" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Describe the change you'd like made to your organisation's portal configuration.
          STX staff will review your request and apply or discuss the change with you.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
          <select className={inp} value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            What would you like changed, and why?
          </label>
          <textarea
            className={inp}
            rows={5}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g. We'd like to enable carbon footprint tracking for our upcoming sustainability reporting. Our board requires quarterly CO₂ data from all departments."
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function OrgSettings() {
  useEffect(() => { document.title = 'Organisation Settings — STX Connect'; }, []);

  const { userProfile } = useAuth();
  const { clientId, clientName } = useTenant();

  const [cfg, setCfg]         = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    getDoc(doc(db, 'clients', clientId, 'config', 'settings')).then(snap => {
      setCfg(snap.exists() ? snap.data() : {});
      setLoading(false);
    });
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const q = query(
      collection(db, 'configRequests'),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [clientId]);

  if (loading) return <p className="text-sm text-gray-600">Loading settings…</p>;
  if (!cfg)    return null;

  const fees        = cfg.fees        || {};
  const workflow    = cfg.workflow    || {};
  const features    = cfg.features    || {};
  const hotel       = cfg.hotelBooking || {};
  const variance    = cfg.policyVariance || {};
  const mfa         = cfg.mfa         || {};
  const tripTypes   = cfg.dropdowns?.tripTypes   || [];
  const sectorTypes = cfg.dropdowns?.sectorTypes || [];
  const costCentres = cfg.dropdowns?.costCentres || [];
  const budgets     = Array.isArray(cfg.budgets) ? cfg.budgets : (cfg.budgets ? [cfg.budgets] : []);

  return (
    <PermissionGate permission={PERMISSIONS.SETTINGS_VIEW}>
      <div className="space-y-6 max-w-3xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Organisation Settings</h1>
            <p className="text-sm text-gray-600 mt-1">
              Your portal configuration — read only. To request a change, use the button on the right.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 shrink-0"
          >
            <Plus size={15} aria-hidden="true" />
            Request a change
          </button>
        </div>

        {/* Features */}
        <Section title="Features & Modules">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(FEATURE_LABELS).map(([key, label]) => (
              <FeatureBadge key={key} label={label} enabled={!!features[key]} />
            ))}
          </div>
        </Section>

        {/* Approval Workflow */}
        <Section title="Approval Workflow">
          <div className="space-y-2">
            {tripTypes.map(type => {
              const required = workflow.approvalByTripType?.[type] ?? workflow.requiresApproval ?? true;
              return (
                <FieldRow
                  key={type}
                  label={type}
                  value={required ? 'Approval required before booking' : 'No approval required'}
                />
              );
            })}
          </div>
          {workflow.escalationEnabled && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Escalation</p>
              <FieldRow label="Reminder after" value={`${workflow.escalationReminderDays ?? 3} days`} />
              <FieldRow label="Escalate to STX after" value={`${workflow.escalationEscalateDays ?? 7} days`} />
            </div>
          )}
          {!workflow.escalationEnabled && (
            <p className="text-sm text-gray-500 mt-1">Approval escalation is not enabled.</p>
          )}
        </Section>

        {/* Cost Centres */}
        <Section title="Cost Centres">
          <Tags values={costCentres} />
        </Section>

        {/* Trip & Sector Types */}
        <Section title="Trip & Sector Types">
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Trip types</p>
              <Tags values={tripTypes} />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Sector types</p>
              <Tags values={sectorTypes} />
            </div>
          </div>
        </Section>

        {/* Fees */}
        <Section title="Fees & Billing">
          {fees.managementFeeEnabled ? (
            <>
              <FieldRow label="Management fee" value={`${fees.managementFeeLabel || 'STX Management Fee'} — $${fees.managementFeeAmount ?? 0} ex-GST`} />
              <FieldRow
                label="Applies to"
                value={fees.managementFeeAppliesTo?.length ? fees.managementFeeAppliesTo.join(', ') : 'All trip types'}
              />
            </>
          ) : (
            <FieldRow label="Management fee" value="Not enabled" />
          )}
          {fees.amendmentFeeEnabled ? (
            <>
              <FieldRow label="Amendment fee" value={`$${fees.amendmentFeeAmount ?? 0} ex-GST`} />
              <FieldRow
                label="Applies to"
                value={fees.amendmentFeeAppliesTo?.length ? fees.amendmentFeeAppliesTo.join(', ') : 'All trip types'}
              />
            </>
          ) : (
            <FieldRow label="Amendment fee" value="Not enabled" />
          )}
          <FieldRow
            label="GST rate"
            value={fees.gstRate === 0 ? 'No GST' : fees.gstRate === 0.15 ? '15% (NZ GST)' : '10% (Australian GST)'}
          />
        </Section>

        {/* Hotel Booking */}
        {features.hotelBooking && (
          <Section title="Hotel Booking">
            <FieldRow
              label="Self-managed trips"
              value={hotel.selfManagedHotelBooking !== false ? 'Hotel booking available' : 'Hotel booking not available'}
            />
          </Section>
        )}

        {/* Policy Variance */}
        <Section title="Policy Variance Thresholds">
          {variance.accommodation?.enabled ? (
            <FieldRow
              label="Accommodation"
              value={`Up to ${variance.accommodation.value}${variance.accommodation.type === 'percent' ? '%' : ' AUD'} over policy rate — ${variance.accommodation.action === 'warn' ? 'shows warning' : 'requires approval'}`}
            />
          ) : (
            <FieldRow label="Accommodation" value="No variance threshold configured" />
          )}
          {variance.flight?.enabled ? (
            <FieldRow
              label="Flights"
              value={`Up to ${variance.flight.value}${variance.flight.type === 'percent' ? '%' : ' AUD'} over policy rate — ${variance.flight.action === 'warn' ? 'shows warning' : 'requires approval'}`}
            />
          ) : (
            <FieldRow label="Flights" value="No variance threshold configured" />
          )}
        </Section>

        {/* MFA */}
        <Section title="Security & Two-Factor Authentication">
          {mfa.enabled ? (
            <FieldRow
              label="Two-factor authentication"
              value={mfa.mode === 'mandatory' ? 'Mandatory — all users must set up 2FA' : 'Optional — users can choose to enable 2FA'}
            />
          ) : (
            <FieldRow label="Two-factor authentication" value="Not enabled" />
          )}
        </Section>

        {/* Annual Budget */}
        {budgets.length > 0 && (
          <Section title="Annual Travel Budget" collapsible>
            <div className="space-y-4">
              {budgets.map((b, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-1.5">
                  <p className="text-sm font-semibold text-gray-700">
                    FY{b.fiscalYear - 1}/{String(b.fiscalYear).slice(2)}
                  </p>
                  <FieldRow label="Overall budget (ex-GST)" value={b.overall ? `$${Number(b.overall).toLocaleString()}` : 'Not set'} />
                  <FieldRow label="Alert threshold" value={b.alertThreshold ? `${b.alertThreshold}%` : 'Not set'} />
                  {b.byCostCentre && Object.keys(b.byCostCentre).length > 0 && (
                    <div className="pt-1.5 space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">By cost centre</p>
                      {Object.entries(b.byCostCentre).map(([cc, amt]) => (
                        <FieldRow key={cc} label={cc} value={`$${Number(amt).toLocaleString()}`} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Change Requests */}
        <Section title="Your change requests">
          {requests.length === 0 ? (
            <p className="text-sm text-gray-500">No requests submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {requests.map(r => (
                <div key={r.id} className="border border-gray-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{r.category}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Submitted {fmtDate(r.createdAt)} by {r.requestedByName}</p>
                    </div>
                    <RequestStatusBadge status={r.status} />
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.description}</p>
                  {r.reviewNote && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mt-1">
                      <p className="text-xs font-semibold text-blue-700 mb-0.5">STX response</p>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap">{r.reviewNote}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

      </div>

      {showModal && (
        <RequestModal
          clientId={clientId}
          clientName={clientName}
          userProfile={userProfile}
          onClose={() => setShowModal(false)}
        />
      )}
    </PermissionGate>
  );
}
